/**
 * videoPlayerRules — the pure half of `ui/VideoPlayer`.
 *
 * Time words, seek maths, where a tap lands, what a key does and the viewer's
 * remembered speed and volume. No React and no DOM beyond the TimeRanges and
 * Storage objects it is handed, so all of it is unit-tested on its own
 * (`__tests__/components/ui/videoPlayerRules.test.js`).
 */

export const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
/** The ⟲ / ⟳ buttons, J / L and one double tap. */
export const SKIP_SECONDS = 10;
/** ← / → */
export const ARROW_SECONDS = 5;
export const VOLUME_STEP = 0.1;
/** Controls fade this long after the last touch or mouse move while playing. */
export const HIDE_CONTROLS_MS = 3000;
/** Two taps closer together than this are a double tap. */
export const DOUBLE_TAP_MS = 300;
/** After a double-tap seek, each further tap on the same side seeks again until the taps stop for this long. */
export const SEEK_STREAK_MS = 700;
/** A resume point this close to the end is a finished video — start it from the top instead. */
export const RESUME_TAIL_SECONDS = 5;

const PREFS_KEY = "vc-video-player";

const finite = (n) => typeof n === "number" && Number.isFinite(n);
const pad2 = (n) => String(n).padStart(2, "0");

/**
 * "4:05", or "1:04:05" past an hour. Pass the video's length as `longest` so
 * the current time takes the same shape as the duration beside it ("0:04:05 /
 * 1:10:00"), and the readout does not change width when the hour ticks over.
 */
export function formatTime(seconds, longest = seconds) {
  const s = finite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = pad2(s % 60);
  if (h > 0 || (finite(longest) && longest >= 3600)) return `${h}:${pad2(m)}:${rest}`;
  return `${m}:${rest}`;
}

/** "1 hour 4 minutes 5 seconds" — what a screen reader speaks for a slider's value. */
export function spokenTime(seconds) {
  const s = finite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  const parts = [];
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) parts.push(`${h} ${h === 1 ? "hour" : "hours"}`);
  if (m) parts.push(`${m} ${m === 1 ? "minute" : "minutes"}`);
  if (sec || !parts.length) parts.push(`${sec} ${sec === 1 ? "second" : "seconds"}`);
  return parts.join(" ");
}

/** The length to show: the file's own once it is known, else the caller's hint, else 0 (unknown). */
export function knownDuration(mediaDuration, hintSeconds = 0) {
  if (finite(mediaDuration) && mediaDuration > 0) return mediaDuration;
  return finite(hintSeconds) && hintSeconds > 0 ? hintSeconds : 0;
}

/** A seek target kept inside the video. An unknown length only stops it going below zero. */
export function clampTime(t, duration) {
  const v = finite(t) ? t : 0;
  if (!(finite(duration) && duration > 0)) return Math.max(0, v);
  return Math.min(duration, Math.max(0, v));
}

/**
 * How far the video is loaded ahead of the playhead: the end of the buffered
 * range the playhead sits in, or 0 when it sits in none (just after a seek).
 * `ranges` is a TimeRanges (or anything with length / start(i) / end(i)).
 */
export function bufferedEnd(ranges, t) {
  try {
    if (!ranges || !ranges.length) return 0;
    for (let i = 0; i < ranges.length; i += 1) {
      // A quarter-second of slack: right after a seek the playhead can sit a
      // hair before the start of the range the browser fetched for it.
      if (ranges.start(i) <= t + 0.25 && t <= ranges.end(i)) return ranges.end(i);
    }
  } catch {
    /* a detached element's TimeRanges can throw on access */
  }
  return 0;
}

/** Where along a bar a pointer is, 0…1. */
export function fractionAt(clientX, rect) {
  if (!rect || !(rect.width > 0)) return 0;
  return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
}

/**
 * Which part of the picture a tap landed in. The outer 40 % on each side seek
 * on a double tap (the YouTube gesture people already know); the middle fifth
 * plays and pauses, so a double tap in the centre is never mistaken for a seek.
 */
export function tapZone(x, width) {
  if (!(width > 0)) return "centre";
  const f = x / width;
  if (f < 0.4) return "back";
  if (f > 0.6) return "forward";
  return "centre";
}

/** The next speed up (+1) or down (−1) the list, starting from the nearest listed one. */
export function nextSpeed(rate, direction) {
  let i = SPEEDS.indexOf(rate);
  if (i < 0) {
    i = SPEEDS.reduce((best, s, idx) => (Math.abs(s - rate) < Math.abs(SPEEDS[best] - rate) ? idx : best), 0);
  }
  return SPEEDS[Math.min(SPEEDS.length - 1, Math.max(0, i + direction))];
}

export const speedLabel = (rate) => `${rate}x`;

/**
 * What a key does to the player, or null for a key it leaves alone.
 *
 * `inside` is whether focus is in the player. Without it ↑ / ↓ / Home / End
 * stay the page's (they scroll it), so a student reading the notes under the
 * video does not change its volume by scrolling.
 */
export function shortcutFor(key, { inside = false } = {}) {
  switch (key) {
    case " ":
    case "k":
    case "K":
      return { type: "toggle" };
    case "ArrowLeft":
      return { type: "seekBy", by: -ARROW_SECONDS };
    case "ArrowRight":
      return { type: "seekBy", by: ARROW_SECONDS };
    case "j":
    case "J":
      return { type: "seekBy", by: -SKIP_SECONDS };
    case "l":
    case "L":
      return { type: "seekBy", by: SKIP_SECONDS };
    case "ArrowUp":
      return inside ? { type: "volumeBy", by: VOLUME_STEP } : null;
    case "ArrowDown":
      return inside ? { type: "volumeBy", by: -VOLUME_STEP } : null;
    case "Home":
      return inside ? { type: "seekTo", fraction: 0 } : null;
    case "End":
      return inside ? { type: "seekTo", fraction: 1 } : null;
    case "m":
    case "M":
      return { type: "mute" };
    case "f":
    case "F":
      return { type: "fullscreen" };
    case ">":
      return { type: "rate", direction: 1 };
    case "<":
      return { type: "rate", direction: -1 };
    default:
      if (/^[0-9]$/.test(key)) return { type: "seekTo", fraction: Number(key) / 10 };
      return null;
  }
}

/** Is this element somewhere the viewer types? Shortcuts never fire there. */
export function isTypingTarget(el) {
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || !!el.isContentEditable;
}

/** The browser's storage, or null where it is blocked (private mode, sandboxed previews). */
export function browserStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * The viewer's speed and volume from last time, validated — a hand-edited or
 * stale entry falls back to 1x at full volume rather than reaching the element.
 * Mute is deliberately not remembered: a video that opens silent reads as broken.
 */
export function readPrefs(storage) {
  const fallback = { rate: 1, volume: 1 };
  try {
    const raw = storage && storage.getItem(PREFS_KEY);
    if (!raw) return fallback;
    const p = JSON.parse(raw) || {};
    return {
      rate: SPEEDS.includes(p.rate) ? p.rate : 1,
      volume: finite(p.volume) && p.volume > 0 && p.volume <= 1 ? p.volume : 1,
    };
  } catch {
    return fallback;
  }
}

export function writePrefs(storage, prefs) {
  try {
    if (storage) storage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* storage full or blocked — a convenience, never an error */
  }
}
